import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import Analytics from '@hh.ru/analytics-js';
import { makeSetStoreField } from '@hh.ru/redux-create-reducer';
import { usePush } from '@hh.ru/redux-spa-middleware';
import { Breakpoint, getBreakpoint } from 'bloko/common/media';
import urlParser from 'bloko/common/urlParser';

import paths from 'src/app/routePaths';
import { ClustersSetting } from 'src/components/Search/Common/Filters/utils';
import { useSelector } from 'src/hooks/useSelector';
import { WITH_TOP_FILTER_CATALOG } from 'src/models/topFilterCatalog';

const searchLoadingAction = makeSetStoreField('searchLoading');

export default (): ((value: string) => void) => {
    const dispatch = useDispatch();
    const { pathname, query: routerLocationQuery } = useSelector((state) => state.router.location);
    const searchResultQuery = useSelector((state) => state.vacancySearchResult.queries.searchPeriodBase);
    const enableSimilarSavedSearch = useSelector((state) => state.vacancySearchResult.enableSimilarSavedSearch);
    const searchPeriod = useSelector((state) => state.vacancySearchResult.criteria?.search_period);
    const defaultHhtmSource = useSelector((state) => state.analyticsParams.hhtmSource);
    const isSearchCatalog = useSelector((state) => state.isSearchCatalog);
    const push = usePush();

    const breakpoint = getBreakpoint();

    return useCallback(
        (value) => {
            const isSXS = [Breakpoint.XS, Breakpoint.S].includes(breakpoint);
            const buttonName = `vacancy_search_filter_${isSXS ? 'touch' : 'apply'}`;
            const hhtmSource = isSXS ? `vacancy_search_filter` : defaultHhtmSource;
            Analytics.sendHHEventButtonClick(buttonName, {
                type: 'SEARCH_PERIOD',
                oldValue: searchPeriod,
                newValue: value,
                hhtmSource,
            });

            const url = urlParser(`${pathname}?${searchResultQuery ?? ''}&search_period=${value}`);
            if ('date_from' in url.params) {
                // date_from and search_period not compatible
                delete url.params.date_from;
            }
            if (enableSimilarSavedSearch) {
                url.params[ClustersSetting.ForceFiltersSaving] = ['true'];
            }
            if (WITH_TOP_FILTER_CATALOG in routerLocationQuery) {
                url.params[WITH_TOP_FILTER_CATALOG] = routerLocationQuery[WITH_TOP_FILTER_CATALOG];
            }
            if (isSearchCatalog) {
                url.pathname = paths.vacancySearch;
            }
            dispatch(searchLoadingAction(true));
            push(url.href);
        },
        [
            breakpoint,
            defaultHhtmSource,
            searchPeriod,
            pathname,
            searchResultQuery,
            enableSimilarSavedSearch,
            routerLocationQuery,
            isSearchCatalog,
            dispatch,
            push,
        ]
    );
};
